import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../App';
import {
  DeltaBarChart,
  RankedBarChart,
  ScoreGauge,
  TrendLineChart,
  formatCurrency,
} from '../components/Charts';

function CardHint({ text }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <span ref={rootRef} className={open ? 'card-hint is-open' : 'card-hint'}>
      <button
        className="card-hint-trigger"
        type="button"
        aria-label="Show help hint"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        ?
      </button>
      <span className="card-hint-tooltip">{text}</span>
    </span>
  );
}

function HealthDot({ openRecs }) {
  const n = Number(openRecs || 0);
  const cls = n === 0 ? 'health-dot health-dot-green' : n <= 3 ? 'health-dot health-dot-amber' : 'health-dot health-dot-red';
  const label = n === 0 ? 'Optimized' : n <= 3 ? 'Needs attention' : 'Critical backlog';
  return <span className={cls} title={label} aria-label={label} />;
}

function isoWeekString(dateValue) {
  const date = new Date(Date.UTC(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

export default function DashboardPage() {
  const location = useLocation();
  const {
    kpiSavings, kpiOpen, kpiAccepted, kpiSpend, kpiImplemented,
    trendPoints, riskItems, categoryItems, hasAnyAnalytics,
    cloudAccounts, accountBreakdown, topActions,
    weeklySummary, weeklySummaryWeek,
    agingQueue, budgetRisk, allocationConfidence,
    currentUser, currentTenant, securityPosture,
    resetMessages, loadWeeklySummary,
  } = useApp();

  const [weeklyBusy, setWeeklyBusy] = useState(false);
  const [weeklyError, setWeeklyError] = useState('');
  const [weeklyPickerValue, setWeeklyPickerValue] = useState('');
  const [showDetailed, setShowDetailed] = useState(false);
  const roleKey = (currentUser?.role || '').toLowerCase();
  const canViewPostLoginPositioning = ['owner', 'admin', 'superadmin'].includes(roleKey);
  const weekOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    const now = new Date();
    for (let i = 0; i < 16; i += 1) {
      const d = new Date(now);
      d.setDate(now.getDate() - (7 * i));
      const value = isoWeekString(d);
      if (seen.has(value)) continue;
      seen.add(value);
      options.push({ value, label: i === 0 ? `${value} (current)` : value });
    }
    if (weeklySummaryWeek && !seen.has(weeklySummaryWeek)) {
      options.unshift({ value: weeklySummaryWeek, label: weeklySummaryWeek });
    }
    return options;
  }, [weeklySummaryWeek]);

  useEffect(() => {
    resetMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setWeeklyPickerValue(weeklySummaryWeek || '');
  }, [weeklySummaryWeek]);

  useEffect(() => {
    if (location.hash !== '#why-action') return;
    if (!canViewPostLoginPositioning) return;

    const timer = window.setTimeout(() => {
      const target = document.getElementById('why-action');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);

    return () => window.clearTimeout(timer);
  }, [location.hash, canViewPostLoginPositioning]);

  const totalRecommendations = kpiOpen + kpiAccepted;
  const executionRate = totalRecommendations > 0
    ? (kpiAccepted / totalRecommendations) * 100
    : 0;
  const annualizedSavings = kpiSavings * 12;
  const savingsPct = kpiSpend > 0 ? Math.min(Math.round((kpiSavings / kpiSpend) * 100), 100) : null;
  const topRisk = [...riskItems].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const topCategory = [...categoryItems].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const monthlyOpportunity = Number(kpiSavings || 0);
  const actionsReady = Number(topActions?.length || 0);
  const priorityHighRiskCount = topActions.filter((item) => Number(item?.risk_score || 0) >= 70).length;
  const priorityPotentialSavings = topActions.reduce((sum, item) => {
    const value = Number(item?.estimated_monthly_delta_usd || 0);
    return sum + Math.max(value, 0);
  }, 0);
  const accountsConnected = Number(cloudAccounts?.length || 0);
  const decisionConfidence = Math.round(executionRate || 0);
  const uniqueRegions = Array.from(new Set((cloudAccounts || []).map((a) => String(a?.region || '').trim()).filter(Boolean)));
  const selectedRegion = uniqueRegions.length > 0 ? uniqueRegions[0] : 'global';

  async function reloadWeeklySummary(weekValue = '') {
    setWeeklyBusy(true);
    setWeeklyError('');
    try {
      await loadWeeklySummary(weekValue);
    } catch (error) {
      setWeeklyError(error.message || 'Could not load weekly summary.');
    } finally {
      setWeeklyBusy(false);
    }
  }

  return (
    <div className="page-view">
      <div className="page-header">
        <p className="feature-eyebrow">Dashboard</p>
        <h2>{currentTenant?.name || currentUser?.email?.split('@')[1] || 'Workspace'}</h2>
        <p className="page-subtitle">
          Plan: <strong>{securityPosture?.tenant_plan || currentTenant?.plan || 'starter'}</strong>
          &nbsp;&middot;&nbsp;
          Role: <strong>{currentUser?.role || 'viewer'}</strong>
        </p>
      </div>

      <div className="workspace-block dashboard-control-bar">
        <div className="dashboard-control-item">
          <span className="dashboard-control-label">Account</span>
          <strong>{currentTenant?.name || 'Workspace'}</strong>
        </div>
        <div className="dashboard-control-item">
          <span className="dashboard-control-label">Region</span>
          <strong>{selectedRegion}</strong>
        </div>
        <div className="dashboard-control-item">
          <span className="dashboard-control-label">Range</span>
          <strong>Last 30 days</strong>
        </div>
        <div className="dashboard-control-item dashboard-control-item-right">
          <span className="dashboard-control-label">Connected Accounts</span>
          <strong>{accountsConnected.toLocaleString()}</strong>
        </div>
      </div>

      <div className="workspace-block dashboard-compact-controls">
        <p className="kpi-sub" style={{ margin: 0 }}>
          Dashboard is in summary mode for faster decision-making.
        </p>
        <div className="form-actions" style={{ marginTop: '0.55rem' }}>
          <button
            className="secondary-action"
            type="button"
            onClick={() => setShowDetailed((prev) => !prev)}
          >
            {showDetailed ? 'Hide detailed analytics' : 'Show detailed analytics'}
          </button>
          <Link className="primary-action" to="/app/assistant">Ask SROIK Assistant</Link>
        </div>
      </div>

      {/* ── KPI Strip ── */}
      <div className="feature-grid dashboard-kpi-grid">
        <article className="feature-card kpi-card">
          <p className="feature-eyebrow">This Week <CardHint text="Weekly gains snapshot: identified, accepted, realized, and blocked actions for the current ISO week." /></p>
          <div className="form-actions" style={{ marginTop: '0.4rem', marginBottom: '0.4rem', gap: '0.4rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
              <span className="feature-eyebrow" style={{ fontSize: '0.68rem' }}>Week</span>
              <select
                value={weeklyPickerValue}
                onChange={(e) => setWeeklyPickerValue(e.target.value)}
                disabled={weeklyBusy}
              >
                {weekOptions.map((week) => (
                  <option key={week.value} value={week.value}>{week.label}</option>
                ))}
              </select>
            </label>
            <button
              className="secondary-action"
              type="button"
              disabled={weeklyBusy}
              onClick={() => reloadWeeklySummary(weeklyPickerValue)}
            >
              {weeklyBusy ? 'Loading...' : 'Load'}
            </button>
            <button
              className="secondary-action"
              type="button"
              disabled={weeklyBusy}
              onClick={() => reloadWeeklySummary('')}
            >
              Current week
            </button>
          </div>
          <h3 className="kpi-value">
            {weeklySummary
              ? `${Number(weeklySummary.realized_count || 0)} realized`
              : '—'}
          </h3>
          <p className="kpi-sub">
            {weeklySummary
              ? `Identified ${Number(weeklySummary.identified_count || 0)} · Accepted ${Number(weeklySummary.accepted_count || 0)} · Blocked ${Number(weeklySummary.blocked_count || 0)}`
              : 'Weekly summary loading'}
          </p>
          {weeklySummary && (
            <p className="kpi-sub">Week {weeklySummary.week} · Realized ${Number(weeklySummary.realized_savings_usd || 0).toLocaleString()}</p>
          )}
          {weeklyError && <p className="auth-feedback auth-error">{weeklyError}</p>}
        </article>

        <article className="feature-card kpi-card">
          <p className="feature-eyebrow">Monthly Spend <CardHint text="Total cloud infrastructure cost across all connected accounts over the last 30 days, pulled directly from each provider's billing API (AWS Cost Explorer, GCP Billing). Reflects actual invoiced charges, not estimates." /></p>
          <h3 className="kpi-value">{kpiSpend > 0 ? `$${kpiSpend.toLocaleString()}` : '—'}</h3>
          <p className="kpi-sub">All accounts · last 30 days</p>
        </article>

        <article className="feature-card kpi-card kpi-card-savings">
          <p className="feature-eyebrow">Potential Savings <CardHint text="Estimated monthly cost you could eliminate by acting on open recommendations. Calculated from actual resource sizes and provider pricing. Only counts open (unactioned) items." /></p>
          <h3 className="kpi-value">
            {kpiSavings > 0 ? `$${kpiSavings.toLocaleString()}` : '—'}
          </h3>
          <p className="kpi-sub">
            {savingsPct !== null && kpiSavings > 0
              ? <span className="savings-pct-pill">{savingsPct}% of spend</span>
              : 'Open recommendations only'}
          </p>
        </article>

        <article className="feature-card kpi-card">
          <p className="feature-eyebrow">Annualized Opportunity <CardHint text="Potential monthly savings × 12 — the full-year value if every open recommendation is implemented today. Useful for budget and business-case discussions." /></p>
          <h3 className="kpi-value">{annualizedSavings > 0 ? formatCurrency(annualizedSavings) : '—'}</h3>
          <p className="kpi-sub">12-month forward projection</p>
        </article>

        <article className="feature-card kpi-card kpi-card-implemented">
          <p className="feature-eyebrow">Implemented Savings <CardHint text="Monthly cost reduction already captured — sum of estimated_monthly_delta_usd across all recommendations marked implemented. This is realized value, not projected." /></p>
          <h3 className="kpi-value">{kpiImplemented > 0 ? `$${kpiImplemented.toLocaleString()}` : '—'}</h3>
          <p className="kpi-sub">Realized monthly value</p>
        </article>

        <article className="feature-card kpi-card">
          <p className="feature-eyebrow">Execution Rate <CardHint text="Accepted ÷ (open + accepted) × 100. Measures how efficiently your team converts findings into action. Below 50% indicates a growing backlog." /></p>
          <h3 className="kpi-value">{totalRecommendations > 0 ? `${Math.round(executionRate)}%` : '—'}</h3>
          <p className="kpi-sub">{kpiAccepted.toLocaleString()} of {totalRecommendations.toLocaleString()} actioned</p>
        </article>
      </div>

      {/* ── Executive Brief ── */}
      <div className="feature-grid analytics-grid executive-grid">
        <article className="feature-card chart-card chart-card-full executive-brief-card">
          <p className="feature-eyebrow">Executive Brief <CardHint text="Quick-glance summary of the most critical signals — highest-risk area, top optimization category, and pending action items." /></p>
          <h3>Board-ready highlights</h3>
          <div className="brief-grid">
            <div className="brief-signal">
              <span className="brief-label">Primary risk concentration</span>
              <strong className="brief-value">{topRisk ? `${topRisk.bucket || topRisk.label}` : '—'}</strong>
              {topRisk && <span className="brief-count">{topRisk.count} recommendations</span>}
            </div>
            <div className="brief-signal">
              <span className="brief-label">Top optimization category</span>
              <strong className="brief-value">{topCategory ? `${topCategory.category || topCategory.label}` : '—'}</strong>
              {topCategory && <span className="brief-count">{topCategory.count} items</span>}
            </div>
            <div className="brief-signal">
              <span className="brief-label">Open action items</span>
              <strong className="brief-value" style={{ color: kpiOpen > 5 ? '#f1c75b' : '#4cc1e4' }}>
                {kpiOpen.toLocaleString()}
              </strong>
              <span className="brief-count">pending review</span>
            </div>
          </div>
        </article>
      </div>

      {/* ── Post-login Positioning ── */}
      {canViewPostLoginPositioning && showDetailed && (
        <div className="feature-grid analytics-grid executive-grid">
          <article id="why-action" className="feature-card chart-card chart-card-full executive-brief-card">
            <p className="feature-eyebrow">Why Teams Move From Visibility To Action</p>
            <h3>You already know where money goes. This section shows what to change next, with lower execution risk.</h3>
            <div className="brief-grid">
              <div>
                <strong>Visibility tools</strong>
                <p>Strong for reporting and allocation, but implementation plans still rely on manual engineering effort.</p>
              </div>
              <div>
                <strong>Automation-first tools</strong>
                <p>Fast optimization can help, but blind changes can increase production risk and rollback load.</p>
              </div>
              <div>
                <strong>SROIK decision engine</strong>
                <p>Workload-aware recommendations designed for engineer review, controlled rollout, and safer execution.</p>
              </div>
            </div>
            <div className="brief-grid" style={{ marginTop: '0.75rem' }}>
              <div>
                <span className="brief-label">Actions ready now</span>
                <strong className="brief-value">{actionsReady}</strong>
                <span className="brief-count">highest-value recommendations pending action</span>
              </div>
              <div>
                <span className="brief-label">Monthly opportunity</span>
                <strong className="brief-value">{monthlyOpportunity > 0 ? formatCurrency(monthlyOpportunity) : '—'}</strong>
                <span className="brief-count">estimated savings from currently open recommendations</span>
              </div>
              <div>
                <span className="brief-label">
                  Execution confidence <CardHint text="Formula: accepted / (accepted + open) x 100. A 0% value means no recommendations have been accepted yet, not that data is missing." />
                </span>
                <strong className="brief-value">{totalRecommendations > 0 ? `${decisionConfidence}%` : '—'}</strong>
                <span className="brief-count">derived from accepted vs open recommendations across {accountsConnected} connected account{accountsConnected === 1 ? '' : 's'}</span>
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: '0.9rem' }}>
              <Link className="primary-action" to="/app/recommendations">Review top actions for this environment</Link>
            </div>
          </article>
        </div>
      )}

      {/* ── Priority Actions ── */}
      {topActions.length > 0 && (
        <div className="feature-grid analytics-grid">
          <article className="feature-card chart-card chart-card-full">
            <p className="feature-eyebrow">Top Priority Actions <CardHint text="Dashboard summary is intentionally count-focused for large fleets. Review individual recommendations in the Recommendations page." /></p>
            <h3>Highest-value findings (summary)</h3>
            <div className="brief-grid" style={{ marginTop: '0.5rem' }}>
              <div className="brief-signal">
                <span className="brief-label">Items in priority set</span>
                <strong className="brief-value">{actionsReady.toLocaleString()}</strong>
                <span className="brief-count">top-ranked recommendations currently shown</span>
              </div>
              <div className="brief-signal">
                <span className="brief-label">High-risk items</span>
                <strong className="brief-value">{priorityHighRiskCount.toLocaleString()}</strong>
                <span className="brief-count">priority findings with risk score 70+</span>
              </div>
              <div className="brief-signal">
                <span className="brief-label">Combined monthly savings</span>
                <strong className="brief-value">
                  {priorityPotentialSavings > 0 ? formatCurrency(priorityPotentialSavings) : '—'}
                </strong>
                <span className="brief-count">aggregate estimated savings for the priority set</span>
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: '0.9rem' }}>
              <Link className="primary-action" to="/app/recommendations">Open detailed recommendations</Link>
            </div>
          </article>
        </div>
      )}

      {/* ── FinOps Accountability ── */}
      {showDetailed && (
      <div id="reports" className="feature-grid analytics-grid">
        <article className="feature-card chart-card">
          <p className="feature-eyebrow">Aging Actions <CardHint text="Open and accepted recommendations grouped by age buckets so teams can burn down old backlog first." /></p>
          <h3>Backlog age profile</h3>
          <p className="kpi-sub">Median action age: <strong>{Number(agingQueue?.median_age_days || 0).toLocaleString()} days</strong></p>
          <div className="aging-bucket-row">
            {(agingQueue?.buckets || []).map((bucket) => (
              <div key={bucket.bucket} className="aging-bucket-pill">
                <span>{bucket.bucket} days</span>
                <strong>{Number(bucket.count || 0).toLocaleString()}</strong>
              </div>
            ))}
          </div>
          {(agingQueue?.top_overdue_actions || []).length > 0 ? (
            <table className="account-breakdown-table finops-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Age</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Savings</th>
                </tr>
              </thead>
              <tbody>
                {agingQueue.top_overdue_actions.map((item) => (
                  <tr key={item.recommendation_id}>
                    <td>{item.title}</td>
                    <td>{Number(item.age_days || 0)}d</td>
                    <td><span className="category-pill">{item.status}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(item.estimated_monthly_delta_usd || 0) > 0
                        ? `$${Number(item.estimated_monthly_delta_usd).toLocaleString()}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-state" style={{ marginTop: '0.5rem' }}>No aging actions in scope.</p>
          )}
        </article>

        <article className="feature-card chart-card">
          <p className="feature-eyebrow">At Risk This Month <CardHint text="Forecasted budget pressure by account, with linked recommendation IDs for quick follow-up." /></p>
          <h3>Budget risk forecast</h3>
          <p className="kpi-sub">
            At risk: <strong>{Number(budgetRisk?.at_risk_count || 0).toLocaleString()}</strong>
            &nbsp;&middot;&nbsp;Healthy: <strong>{Number(budgetRisk?.healthy_count || 0).toLocaleString()}</strong>
          </p>
          {(budgetRisk?.items || []).length > 0 ? (
            <table className="account-breakdown-table finops-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Tier</th>
                  <th style={{ textAlign: 'right' }}>Projected Overrun</th>
                  <th style={{ textAlign: 'right' }}>Open Recs</th>
                </tr>
              </thead>
              <tbody>
                {budgetRisk.items.slice(0, 5).map((item) => (
                  <tr key={item.cloud_account_id}>
                    <td>{item.provider.toUpperCase()} · {item.account_id}</td>
                    <td><span className="category-pill">{item.risk_tier}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(item.projected_overrun_usd || 0) > 0
                        ? `$${Number(item.projected_overrun_usd).toLocaleString()}`
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{Number(item.open_recommendations || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-state" style={{ marginTop: '0.5rem' }}>No budget-risk data yet.</p>
          )}
        </article>

        <article className="feature-card chart-card">
          <p className="feature-eyebrow">Allocation Confidence <CardHint text="Spend-weighted allocation confidence and top offenders with low coverage percent." /></p>
          <h3>Spend allocatability</h3>
          <p className="kpi-sub">
            Coverage <strong>{Number(allocationConfidence?.spend_weighted_coverage_percent || 0).toLocaleString()}%</strong>
            &nbsp;&middot;&nbsp;
            Allocatable spend <strong>{Number(allocationConfidence?.allocatable_spend_percent || 0).toLocaleString()}%</strong>
          </p>
          <p className="kpi-sub">
            {Number(allocationConfidence?.allocatable_monthly_spend_usd || 0) > 0
              ? `$${Number(allocationConfidence.allocatable_monthly_spend_usd).toLocaleString()} allocatable of $${Number(allocationConfidence.total_monthly_spend_usd || 0).toLocaleString()}`
              : 'No allocatable spend data yet'}
          </p>
          {(allocationConfidence?.offenders || []).length > 0 ? (
            <table className="account-breakdown-table finops-table">
              <thead>
                <tr>
                  <th>Offender</th>
                  <th style={{ textAlign: 'right' }}>Coverage</th>
                  <th style={{ textAlign: 'right' }}>Spend</th>
                </tr>
              </thead>
              <tbody>
                {allocationConfidence.offenders.map((item) => (
                  <tr key={item.cloud_account_id}>
                    <td>{item.provider.toUpperCase()} · {item.account_id}</td>
                    <td style={{ textAlign: 'right' }}>{Number(item.coverage_percent || 0).toLocaleString()}%</td>
                    <td style={{ textAlign: 'right' }}>${Number(item.monthly_spend_usd || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-state" style={{ marginTop: '0.5rem' }}>No allocation offenders found.</p>
          )}
        </article>
      </div>
      )}

      {/* ── Charts ── */}
      {hasAnyAnalytics && showDetailed && (
        <div className="feature-grid analytics-grid">
          {trendPoints.length > 1 && (
            <article className="feature-card chart-card chart-card-wide">
              <p className="feature-eyebrow">Savings Trend <CardHint text="Monthly breakdown of estimated savings from recommendations over the past 6 months. Rising values indicate an increasing backlog of opportunities." /></p>
              <h3>Monthly projection and volatility</h3>
              <TrendLineChart points={trendPoints} />
            </article>
          )}
          {trendPoints.length === 1 && (
            <article className="feature-card chart-card chart-card-full">
              <p className="feature-eyebrow">Savings Trend</p>
              <h3>Monthly projection and volatility</h3>
              <p className="trend-nascent-hint">
                First recommendations recorded in <strong>{trendPoints[0].label}</strong> — trend data builds over time as more months accumulate.
              </p>
            </article>
          )}
          {trendPoints.length > 1 && (
            <article className="feature-card chart-card">
              <p className="feature-eyebrow">Monthly Change <CardHint text="Net delta in savings opportunity month-over-month. Negative bars mean you're executing faster than new issues are discovered — a healthy sign." /></p>
              <h3>Delta bars</h3>
              <DeltaBarChart points={trendPoints} />
            </article>
          )}
          {riskItems.length > 0 && (
            <article className="feature-card chart-card">
              <p className="feature-eyebrow">Execution Health <CardHint text="Score 0–100 measuring how effectively recommendations are converted to action. Below 50% signals a large unaddressed backlog." /></p>
              <h3>Recommendation conversion</h3>
              <ScoreGauge
                score={executionRate}
                label="Execution score"
                subtitle={`${kpiAccepted.toLocaleString()} accepted`}
              />
            </article>
          )}
          {riskItems.length > 0 && (
            <article className="feature-card chart-card">
              <p className="feature-eyebrow">Risk Priority <CardHint text="Distribution by severity: Low (0–34), Medium (35–69), High (70–100). A large High bar demands immediate attention." /></p>
              <h3>Risk concentration</h3>
              <RankedBarChart items={riskItems} labelKey="bucket" valueKey="count" title="Risk" />
            </article>
          )}
          {categoryItems.length > 0 && (
            <article className="feature-card chart-card">
              <p className="feature-eyebrow">Opportunity Mix <CardHint text="Recommendations by category — Cost, Security, Performance. A dominant Cost bar means billing optimization is your biggest quick win." /></p>
              <h3>Category ranking</h3>
              <RankedBarChart items={categoryItems} labelKey="category" valueKey="count" title="Category" />
            </article>
          )}
        </div>
      )}

      {/* ── Account Health Table ── */}
      {accountBreakdown.length > 0 && showDetailed && (
        <div className="feature-grid analytics-grid">
          <article className="feature-card chart-card chart-card-full">
            <p className="feature-eyebrow">Account Health <CardHint text="Spend, open savings, and optimization health per connected cloud account. The health indicator turns amber at 1–3 open items and red at 4+." /></p>
            <h3>Per-account breakdown</h3>
            <table className="account-breakdown-table">
              <thead>
                <tr>
                  <th>Health</th>
                  <th>Provider</th>
                  <th>Account ID</th>
                  <th style={{ textAlign: 'right' }}>Monthly Spend</th>
                  <th style={{ textAlign: 'right' }}>Open Savings</th>
                  <th style={{ textAlign: 'right' }}>Savings %</th>
                  <th style={{ textAlign: 'right' }}>Open Recs</th>
                </tr>
              </thead>
              <tbody>
                {accountBreakdown.map((acc) => {
                  const spend = Number(acc.monthly_spend_usd || 0);
                  const savings = Number(acc.potential_monthly_savings_usd || 0);
                  const pct = spend > 0 && savings > 0
                    ? Math.round((savings / spend) * 100)
                    : null;
                  return (
                    <tr key={acc.cloud_account_id}>
                      <td><HealthDot openRecs={acc.open_recommendations} /></td>
                      <td><span className="provider-badge">{acc.provider.toUpperCase()}</span></td>
                      <td className="account-id-cell">{acc.account_id}</td>
                      <td style={{ textAlign: 'right' }}>{spend > 0 ? `$${spend.toLocaleString()}` : '—'}</td>
                      <td style={{ textAlign: 'right', color: savings > 0 ? '#4cc1e4' : undefined }}>
                        {savings > 0 ? `$${savings.toLocaleString()}` : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {pct !== null ? <span className="savings-pct-pill">{pct}%</span> : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{acc.open_recommendations}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </article>
        </div>
      )}

      {/* ── Empty state ── */}
      {!hasAnyAnalytics && (
        <div className="empty-state">
          <p className="feature-eyebrow">No analytics data yet</p>
          {cloudAccounts.length > 0 ? (
            <>
              <p>Cloud accounts are connected, but no optimization recommendations have been generated yet. Run a sync after credentials are configured.</p>
              <Link className="primary-action" to="/app/accounts">Open cloud accounts</Link>
            </>
          ) : (
            <>
              <p>Connect a cloud account and run a sync to populate recommendations and savings data.</p>
              <Link className="primary-action" to="/app/accounts">Connect cloud account</Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
